// P-SILENT-FAILURE-BATCH (QA-AUDIT-2026-06-01 Area 1 P1) — last-started-wins
// epoch latch, split out of useCloudSync.js so the cross-user-flash guard is a
// real, unit-testable unit (reverting this module breaks both the hook and its
// test).
//
// THE BUG IT CLOSES: useCloudSync's [user?.id] effect calls
//   refreshStudents().finally(() => setCloudLoading(false))
// with NO cancellation. On a fast sign-out → sign-in (parent switching
// accounts), the effect re-fires for user B while user A's fetchStudents() is
// still in flight. When A's promise finally resolves it calls setStudents(A's
// kids) — painting user A's children UNDER user B's session. That's a
// privacy-adjacent cross-user data flash (premium-trust violation).
//
// THE FIX: every fetch claims a monotonically-increasing token via begin().
// Before writing its result into shared React state, the fetch checks
// isCurrent(token) — true ONLY for the most-recently-started fetch. A stale
// fetch (an older account whose effect has since been superseded) sees its
// token is no longer current and suppresses its setStudents/setCloudOffline,
// so only the latest account's data ever paints.
//
// Why an explicit token rather than a bare counter compare at the call site:
// keeping begin()/isCurrent() together makes the invariant ("only the last
// token issued is current") testable in isolation and impossible to get
// subtly wrong (e.g. off-by-one) at multiple call sites.

/**
 * Create a fresh last-started-wins latch. Hold ONE per hook instance (in a
 * ref) so all overlapping fetches share the same monotonic sequence.
 *
 * @returns {{ begin: () => number, isCurrent: (token: number) => boolean }}
 */
export function createEpochLatch() {
  let current = 0;
  return {
    /** Claim the next token. The returned token is the current one until the
     *  next begin() supersedes it. */
    begin() {
      current += 1;
      return current;
    },
    /** True only for the most-recently-issued token. A stale (superseded)
     *  token returns false → its owner must not write shared state. */
    isCurrent(token) {
      return token === current;
    },
  };
}
